import React, { useEffect, useMemo, useState } from 'react';
import { Col, getItemWithExpiration, Row } from '../lib/utils';
import { colors } from '../lib/colors';
import { Body1Bold, Body1Regular, Body3Bold, Body3Regular, CaptionLight, CaptionRegular, Head4 } from '../lib/font';
import SvgIcon from '../components/SvgIcon';
import Dot from '../components/Dot';
import NewTextInputDropDown from '../components/DropDown/NewTextInputDropDown';
import { CodeValueType } from '../types/types';
import { fetchCoachNameList } from '../services/useCenterQueryService';
import useMultipleQueries from '../hook/useMultipleQueries';
import NewColorRoundButton from '../components/Button/NewColorRoundButton';

import { fetchAuthentificationPhoneNumber } from '../services/useMemberQueryService';
import useSingleQuery from '../hook/useSingleQuery';
import MemoTextInput from '../components/TextInput/MemoTextInput';
import ModalMembershipCreate from '../components/MemberProfile/ModalMembershipCreate';
import ModalRegisterPass from './ClassPass/ModalRegisterPass/ModalRegisterPass';
import SubscriptionRegisterModal from '../components/FacilitySubscription/SubscriptionRegisterModal';
import { DATE_REGEXP, GENDER_TYPE, GENDERCODETYPE, OnboardingGoal, REGISTRATION_TYPE } from '../lib/varialbe';
import { MemberShipApiType } from '../api/memberShip';
import MembershipCard from '../components/MemberProfile/MembershipCard';
import { cloneDeep } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { CustomCheckbox } from '../components/Grid/Grid';
import useMemberMutationService from '../services/useMemberMutationService';
import { CreateTempMemberParamsType, FacilitySubscriptionInfoType } from '../api/member';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import FormTextInput from '../components/InputBox/FormTextInput';
import BusinessInfoFooter from '../components/BusinessInfoFooter';
import { useFacilityProductList } from '@/features/facilityProduct/hooks';
import { IFacilityProduct } from '@/features/facilityProduct/types';

type Props = {
    createAlert: any;
};

const MemberTemporaryPage = ({ createAlert }: Props) => {
    const [authorization, setAuthorization] = useState(getItemWithExpiration('authorization'));
    const [authObject, setAuthObject] = useState(getItemWithExpiration('authObject'));
    const navigate = useNavigate();
    const [isModalMembershipCreateVisible, setIsModalMembershipCreateVisible] = useState(false);
    const [isRegisterPassModal, setIsRegisterPassModal] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const queryClient = useQueryClient();

    // 회원 유형 (public: 퍼블릭, pt: 수강)
    const [memberType, setMemberType] = useState<'public' | 'pt'>('pt');

    // 선택 정보 펼침 상태
    const [showOptional, setShowOptional] = useState(false);

    // 이용권 관련
    const [facilitySubscriptions, setFacilitySubscriptions] = useState<FacilitySubscriptionInfoType[]>([]);
    const [isSubscriptionModalVisible, setIsSubscriptionModalVisible] = useState(false);

    // 담당강사
    const [selectCoach, setSelectCoach] = useState<CodeValueType | undefined>(undefined);
    // 이름
    const [name, setName] = useState('');
    // 휴대폰 번호
    const [phoneNum, setPhoneNum] = useState('');
    // 생년월일
    const [birth, setBirth] = useState('');
    // 성별
    const [gender, setGender] = useState<CodeValueType<GENDERCODETYPE> | undefined>(GENDER_TYPE[2]);
    // 키
    const [height, setHeight] = useState('');
    // 시작체중
    const [startWeight, setStartWeight] = useState('');
    // 주소
    const [address, setAddress] = useState('');
    // 메모
    const [memo, setMemo] = useState('');
    // 수강권 리스트
    const [membershipList, setMembershipList] = useState<MemberShipApiType[]>([]);
    // 중복체크
    const [isDuplicateDisabled, setIsDuplicateDisabled] = useState(true);
    const [isDuplicateCheck, setIsDuplicateCheck] = useState(false);
    // 앱 연결 요청 메시지
    const [isSendMessage, setIsSendMessage] = useState(true);

    // 강사 목록
    const { refinedResults } = useMultipleQueries([
        {
            fetchFn: fetchCoachNameList,
            params: { centerId: authObject?.centerId, isAllConcat: true },
            enabled: !!authObject?.centerId,
        },
    ]);

    // 시설 상품 목록 (useFacilityProductList hook — SubscriptionRegisterModal과 동일)
    const { data: productData } = useFacilityProductList({
        centerId: authObject?.centerId ?? '',
        enabled: !!authObject?.centerId,
    });
    const facilityProducts: IFacilityProduct[] = useMemo(
        () => (productData?.results ?? []).filter((p: IFacilityProduct) => p.isActive && !p.isDeleted),
        [productData]
    );

    const { refinedResult: asyncResult } = useSingleQuery({
        fetchFn: fetchAuthentificationPhoneNumber,
        params: { centerId: authObject.centerId, phoneNumber: phoneNum },
        enabled: false,
    });
    const { createTempMemberMutation } = useMemberMutationService({
        onCreateTempMemberSuccess(data) {
            const memberId = data?.id;
            createAlert('회원 등록이 완료되었어요!', '회원 등록이 완료되었어요!', false, '프로필 바로가기', () => {
                navigate(`/member/${memberId}`, { state: { memberId: memberId }, replace: true });
            });
            queryClient.invalidateQueries({ queryKey: ['getCenterMemberListAPIQuery'] });
            queryClient.invalidateQueries({ queryKey: ['publicMember'] });
        },
        onCreateTempMemberError({ status, data }) {
            const serverMessage = data?.message;

            if (status === 403 && serverMessage === 'Public member feature is disabled') {
                createAlert(
                    '퍼블릭 회원 기능이 비활성화되어 있어요!',
                    '백엔드 서버에서 PUBLIC_MEMBERS_ENABLED 설정이 꺼져 있어 등록할 수 없어요.',
                    true,
                    '확인',
                    () => {}
                );
                return;
            }

            if (status === 406) {
                createAlert('회원 등록 권한이 없어요!', serverMessage || '회원 등록 권한을 확인해주세요.', true, '확인', () => {});
                return;
            }

            createAlert(
                '회원 등록에 실패했어요!',
                serverMessage || '회원 등록 중 오류가 발생했어요. 다시 시도해주세요.',
                true,
                '확인',
                () => {}
            );
        },
    });

    const dropDownCoachList = useMemo(
        () => (refinedResults?.[0]?.data as CodeValueType<string>[]) ?? [],
        [refinedResults]
    );

    // 중복 체크
    const onClickDuplicateCheck = async () => {
        const result: any = await asyncResult.refetch();
        const status = result?.data?.status;
        if (status === 202) {
            setIsDuplicateCheck(true);
            createAlert('신규 등록이 가능한 번호에요!', '새로운 회원 등록이 가능한 번호에요!', false, '확인', () => {});
        } else if (status === 413) {
            createAlert(
                '이미 센터에 등록된 회원이에요!',
                '이미 등록 완료된 회원이에요! 등록된 회원이 아니라면,\n번호가 정확히 기입되었는지 다시 한번 확인해 주세요!',
                true, '확인', () => { setIsDuplicateCheck(false); }
            );
        }
    };

    // 수강권 등록
    const onClickMembershipCreate = () => {
        if (!name) {
            createAlert('이름을 입력해주세요!', '회원 이름을 입력해주세요!', true, '확인', () => {});
            return;
        }
        if (memberType === 'pt' && !selectCoach) {
            createAlert('담당 강사를 선택해주세요!', '담당 강사를 선택해주세요!', true, '확인', () => {});
            return;
        }
        setIsModalMembershipCreateVisible(true);
    };

    const onMembershipCreateSuccess = (membership: MemberShipApiType) => {
        setMembershipList([...membershipList, membership]);
        setIsModalMembershipCreateVisible(false);
        createAlert('수강권 등록이 완료되었어요!', '수강권 등록이 완료되었어요!', false, '확인', () => {});
    };

    // 회원 등록 완료
    const onClickMemberCreate = () => {
        if (name.length < 2 || name.length > 5 || !name) {
            createAlert('이름을 확인해주세요!', '이름은 2 ~ 5글자로 입력해주세요!', true, '확인', () => {});
            return;
        }
        if (memberType === 'pt' && !selectCoach) {
            createAlert('담당 강사를 선택해주세요!', '담당 강사를 선택해주세요!', true, '확인', () => {});
            return;
        }
        if (!phoneNum) {
            createAlert('휴대폰 번호를 입력해주세요!', '휴대폰 번호를 입력해주세요!', true, '확인', () => {});
            return;
        }
        if (!isDuplicateCheck) {
            createAlert('휴대폰 번호 중복체크를 해주세요!', '휴대폰 번호 중복체크를 해주세요!', true, '확인', () => {});
            return;
        }
        if (birth && !DATE_REGEXP.test(birth)) {
            createAlert('생년월일을 정확히 입력해주세요!', '생년월일을 정확히 입력해주세요!', true, '확인', () => {});
            return;
        }

        const params: CreateTempMemberParamsType = {
            centerId: authObject.centerId,
            tempMember: {
                name: name,
                coachId: memberType === 'pt' ? (selectCoach?.code ?? '') : '',
                phoneNumber: phoneNum,
                memberType: memberType === 'public' ? 'public' : undefined,
                gender: gender?.code ?? '',
                height: height ? Number(height) : undefined,
                weight: startWeight ? Number(startWeight) : undefined,
                address: address,
                memo: memo,
                birthday: birth ? dayjs(birth).toISOString() : undefined,
                membershipInfo: memberType === 'pt' ? membershipList : [],
                facilitySubscriptionInfo: facilitySubscriptions.length > 0 ? facilitySubscriptions : undefined,
                isSendAlimtalkToMember: isSendMessage,
            },
        };

        createTempMemberMutation.mutate(params);
    };

    // 휴대폰 자동 포맷
    useEffect(() => {
        const phoneNumReg = /^\d{3}-\d{4}-\d{4}$/;
        setIsDuplicateDisabled(!phoneNumReg.test(phoneNum));

        if (phoneNum) {
            if (phoneNum.length === 10) setPhoneNum(phoneNum.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3'));
            if (phoneNum.length === 13) setPhoneNum(phoneNum.replace(/-/g, '').replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3'));
        }
    }, [phoneNum]);

    // 생년월일 자동 포맷
    useEffect(() => {
        if (birth) {
            if (birth.length === 8) setBirth(birth.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
            if (birth.length === 9) setBirth(birth.replace(/-/g, ''));
        }
    }, [birth]);

    const onManyMemberRegister = () => {
        window.open('https://mustmove.notion.site/5bcbea791bfb4230b6fab4a77cd54534', '_blank', 'noopener,noreferrer');
    };

    // 이용권 발급 (모달에서 상세 입력 후 목록에 추가 — 서버 등록은 회원 등록 완료 시 함께)
    const onCollectSubscription = (data: FacilitySubscriptionInfoType) => {
        setFacilitySubscriptions((prev) => [...prev, data]);
        createAlert('이용권 등록이 완료되었어요!', '회원 등록 완료 시 이용권도 함께 등록돼요!', false, '확인', () => {});
    };

    return (
        <Col style={{ width: '100%', height: '100%', backgroundColor: colors.WHITE_200, marginTop: 64, alignItems: 'center', overflow: 'auto' }}>
            <Col className="flex w-[1192px] mt-[48px] pb-[80px]">
                {/* 헤더 */}
                <Row className="flex w-full mb-[24px]">
                    <Row className="flex w-full items-center">
                        <Row className="items-center cursor-pointer mr-[16px]" onClick={() => window.history.back()}>
                            <SvgIcon name={'SvgLeftStrokeArrow'} size={20} stroke={colors.black} fill={colors.trans} />
                        </Row>
                        <Head4 className="mr-[32px]">회원 등록하기</Head4>
                        <Row className="items-center cursor-pointer" onClick={onManyMemberRegister}>
                            <SvgIcon name={'SvgQuestion'} size={16} stroke={colors.primary} fill={colors.trans} className="mr-[8px]" />
                            <CaptionRegular className="text-BLUE_500">한번에 다수의 회원을 등록하는 방법</CaptionRegular>
                        </Row>
                    </Row>
                </Row>

                {/* ─── 본문 ─── */}
                <Col className="bg-WHITE_50 border border-solid border-BLUE_100 py-[40px] px-[80px] rounded-[16px]">

                    {/* ━━━ 섹션 1: 회원 유형 ━━━ */}
                    <Body1Bold className="text-LAVEL_4 mb-[20px]">회원 유형</Body1Bold>
                    <Row className="gap-[12px] mb-[32px]">
                        {/* PT */}
                        <Col
                            className={`flex-1 items-center justify-center px-[24px] py-[16px] rounded-[12px] border-[2px] cursor-pointer transition-all ${
                                memberType === 'pt'
                                    ? 'border-[#2563EB] bg-[#DBEAFE]'
                                    : 'border-[#E2E8F0] bg-[#F1F5F9] hover:border-[#CBD5E1]'
                            }`}
                            onClick={() => setMemberType('pt')}
                        >
                            <Row className="items-center gap-[10px]">
                                <span style={{ fontSize: 22 }}>💪</span>
                                <Col>
                                    <Body3Bold className={memberType === 'pt' ? 'text-[#1D4ED8]' : 'text-LAVEL_4'}>수강 회원 (PT)</Body3Bold>
                                    <CaptionRegular className="text-LAVEL_2">담당 강사 배정 · 수강권 등록</CaptionRegular>
                                </Col>
                            </Row>
                        </Col>
                        {/* Public */}
                        <Col
                            className={`flex-1 items-center justify-center px-[24px] py-[16px] rounded-[12px] border-[2px] cursor-pointer transition-all ${
                                memberType === 'public'
                                    ? 'border-[#059669] bg-[#D1FAE5]'
                                    : 'border-[#E2E8F0] bg-[#F1F5F9] hover:border-[#CBD5E1]'
                            }`}
                            onClick={() => {
                                if (memberType === 'pt' && (selectCoach || membershipList.length > 0)) {
                                    const ok = window.confirm('퍼블릭 회원으로 전환하면 입력한 담당 강사와 수강권 정보가 초기화됩니다.\n계속하시겠습니까?');
                                    if (!ok) return;
                                }
                                setMemberType('public');
                                setSelectCoach(undefined);
                                setMembershipList([]);
                            }}
                        >
                            <Row className="items-center gap-[10px]">
                                <span style={{ fontSize: 22 }}>🏃</span>
                                <Col>
                                    <Body3Bold className={memberType === 'public' ? 'text-[#059669]' : 'text-LAVEL_4'}>퍼블릭 회원</Body3Bold>
                                    <CaptionRegular className="text-LAVEL_2">시설 이용 전용 · 이용권 등록</CaptionRegular>
                                </Col>
                            </Row>
                        </Col>
                    </Row>

                    {/* 구분선 */}
                    <div className="w-full h-[1px] bg-[#E2E8F0] mb-[32px]" />

                    {/* ━━━ 섹션 2: 필수 정보 ━━━ */}
                    <Body1Bold className="text-LAVEL_4 mb-[20px]">필수 정보</Body1Bold>

                    {/* 이름 + 담당강사 (PT일 때) */}
                    <Row className="mb-[24px] gap-[40px]">
                        <FormTextInput
                            title="이름"
                            placeholder="2 ~ 5글자로 입력해주세요!"
                            value={name}
                            onChangeText={setName}
                            width={320}
                            isRequired
                            maxLength={5}
                        />
                        {memberType === 'pt' && (
                            <Col>
                                <Row className="flex mb-[8px]">
                                    <Body3Regular className="text-LAVEL_4 mr-[4px]">담당 강사</Body3Regular>
                                    <Dot size={4} color="ERROR" className="self-start mr-[12px]" />
                                </Row>
                                <NewTextInputDropDown
                                    selectData={selectCoach}
                                    datas={dropDownCoachList ?? []}
                                    onClickSort={(data) => setSelectCoach(data)}
                                    width={300}
                                    fontType={'Body2Regular'}
                                    placeholder="담당 강사를 선택해주세요"
                                />
                            </Col>
                        )}
                    </Row>

                    {/* 휴대폰 번호 + 중복체크 */}
                    <Row className="mb-[24px] items-end gap-[12px]">
                        <FormTextInput
                            title="휴대폰 번호"
                            placeholder="000-0000-0000"
                            value={phoneNum}
                            onChangeText={setPhoneNum}
                            width={320}
                            isRequired
                            subTitle="정확한 번호를 입력해주셔야 회원님과 라포 연결이 가능해요!"
                            maxLength={13}
                        />
                        <NewColorRoundButton
                            bgColor={isDuplicateDisabled ? 'WHITE_700' : 'primary'}
                            textColor="white"
                            text="중복 체크"
                            onClick={onClickDuplicateCheck}
                            width={96}
                            height={36}
                            className="self-end"
                            disabled={isDuplicateDisabled}
                        />
                    </Row>

                    {/* 구분선 */}
                    <div className="w-full h-[1px] bg-[#E2E8F0] mb-[24px] mt-[8px]" />

                    {/* ━━━ 섹션 3: 선택 정보 (접힘/펼침) ━━━ */}
                    <Row
                        className="items-center cursor-pointer mb-[16px] group"
                        onClick={() => setShowOptional(!showOptional)}
                    >
                        <Body3Bold className="text-LAVEL_3 mr-[8px]">선택 정보</Body3Bold>
                        <CaptionRegular className="text-LAVEL_2 mr-[8px]">
                            생년월일 · 성별 · 키 · 체중 · 주소 · 메모
                        </CaptionRegular>
                        <SvgIcon
                            name={showOptional ? 'SvgUpStrokeArrow' : 'SvgDownStrokeArrow'}
                            size={16}
                            stroke={colors.LAVEL_2}
                            fill={colors.trans}
                        />
                    </Row>

                    {showOptional && (
                        <Col className="mb-[8px] p-[24px] bg-[#F8FAFC] rounded-[12px] border border-[#E2E8F0]">
                            {/* 생년월일 + 성별 */}
                            <Row className="mb-[24px] gap-[40px]">
                                <FormTextInput
                                    title="생년월일"
                                    placeholder="0000-00-00"
                                    value={birth}
                                    onChangeText={setBirth}
                                    width={320}
                                    maxLength={10}
                                />
                                <Col>
                                    <Body3Regular className="text-LAVEL_4 mb-[12px]">성별</Body3Regular>
                                    <NewTextInputDropDown
                                        datas={GENDER_TYPE}
                                        selectData={gender}
                                        onClickSort={(item) => setGender(item)}
                                        fontType="Body2Regular"
                                        width={300}
                                    />
                                </Col>
                            </Row>
                            {/* 키 + 체중 */}
                            <Row className="mb-[24px] gap-[40px]">
                                <FormTextInput
                                    title="키"
                                    placeholder="172"
                                    value={height}
                                    onChangeText={setHeight}
                                    width={320}
                                    maxLength={5}
                                />
                                <FormTextInput
                                    title="시작체중"
                                    placeholder="70"
                                    value={startWeight}
                                    onChangeText={setStartWeight}
                                    width={320}
                                    maxLength={10}
                                />
                            </Row>
                            {/* 주소 */}
                            <FormTextInput
                                title="주소"
                                placeholder="주소를 입력해주세요"
                                value={address}
                                onChangeText={setAddress}
                                width={640}
                                mb={24}
                            />
                            {/* 메모 */}
                            <Col>
                                <Body3Regular className="text-LAVEL_4">메모</Body3Regular>
                                <CaptionLight className="text-LAVEL_2 mb-[8px]">
                                    관리자님과 담당 강사님만 확인할 수 있는 정보에요!
                                </CaptionLight>
                                <MemoTextInput
                                    value={memo}
                                    onChange={(e) => setMemo(e.target.value)}
                                    height={100}
                                    width={780}
                                    placeholder="회원님에 대해 기억해야 할 내용들을 작성해주세요!"
                                />
                            </Col>
                        </Col>
                    )}

                    {/* 구분선 */}
                    <div className="w-full h-[1px] bg-[#E2E8F0] mb-[24px] mt-[16px]" />

                    {/* ━━━ 섹션 4: 수강권 (PT만) ━━━ */}
                    {memberType === 'pt' && (
                        <>
                            <Body3Regular className="text-LAVEL_4">수강권</Body3Regular>
                            <CaptionLight className="text-LAVEL_2">
                                {`회원님이 '라포 앱'으로 수업 신청을 하기 위해 필요해요!`}
                            </CaptionLight>
                            <CaptionLight className="text-LAVEL_2 mb-[12px]">
                                {`회원 등록 후, [회원 프로필 → 수강권 정보] 또는 강사님의 라포 앱으로도 등록이 가능해요!`}
                            </CaptionLight>
                            <Row className="mb-[24px] overflow-auto">
                                {membershipList?.map((membership: MemberShipApiType, index: number) => (
                                    <MembershipCard
                                        key={index}
                                        index={index}
                                        membership={membership}
                                        onClickDetailPage={() => {}}
                                        onClickDelete={(_index: number) => {
                                            const cloneMembershipList = cloneDeep(membershipList);
                                            const newMembershipList = cloneMembershipList.filter((_, i) => i !== _index);
                                            setMembershipList(newMembershipList);
                                        }}
                                    />
                                ))}
                                <Col
                                    className="flex flex-col w-[310px] h-[160px] border-BLUE_200 border-spacing-50 border-dashed border-[1px] bg-BLUE_50 justify-center items-center rounded-[8px] flex-shrink-0 cursor-pointer"
                                    onClick={onClickMembershipCreate}
                                >
                                    <NewColorRoundButton
                                        text="수강권 발급하기"
                                        fontType={'CaptionRegular'}
                                        width={112}
                                        height={24}
                                        bgColor="primary"
                                        textColor="white"
                                    />
                                </Col>
                            </Row>
                            <div className="w-full h-[1px] bg-[#E2E8F0] mb-[24px]" />
                        </>
                    )}

                    {/* ━━━ 섹션 5: 이용권 — PT, 퍼블릭 둘 다 ━━━ */}
                    <Body3Regular className="text-LAVEL_4">이용권</Body3Regular>
                    <CaptionLight className="text-LAVEL_2 mb-[12px]">
                        헬스장, 라커, 운동복 등 시설 이용권을 등록합니다. 회원 등록 후에도 추가할 수 있어요!
                    </CaptionLight>

                    {/* 등록된 이용권 카드 */}
                    {facilitySubscriptions.length > 0 && (
                        <Col className="gap-[8px] mb-[12px]">
                            {facilitySubscriptions.map((sub, idx) => (
                                <Row
                                    key={idx}
                                    className="items-center justify-between p-[12px] bg-[#F0FDF4] border border-[#BBF7D0] rounded-[8px]"
                                >
                                    <Row className="items-center gap-[12px]">
                                        <span style={{ fontSize: 16 }}>
                                            {sub.category === 'gym' ? '🏋️' : sub.category === 'locker' ? '🔒' : sub.category === 'clothing' ? '👕' : '📦'}
                                        </span>
                                        <Col>
                                            <Body3Bold className="text-LAVEL_4">{sub.productName}</Body3Bold>
                                            <CaptionRegular className="text-LAVEL_2">
                                                {sub.startDate} ~ {sub.endDate} · {sub.price.toLocaleString()}원 · {sub.paymentMethod === 'card' ? '카드' : sub.paymentMethod === 'cash' ? '현금' : sub.paymentMethod === 'transfer' ? '계좌이체' : sub.paymentMethod}
                                            </CaptionRegular>
                                        </Col>
                                    </Row>
                                    <button
                                        onClick={() => setFacilitySubscriptions(facilitySubscriptions.filter((_, i) => i !== idx))}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#EF4444' }}
                                    >
                                        ✕
                                    </button>
                                </Row>
                            ))}
                        </Col>
                    )}

                    {/* 이용권 발급하기 — 모달에서 상세 정보 입력 */}
                    {facilityProducts.length > 0 ? (
                        <Row className="mb-[16px]">
                            <Col
                                className="flex flex-col w-[310px] h-[160px] border-BLUE_200 border-spacing-50 border-dashed border-[1px] bg-BLUE_50 justify-center items-center rounded-[8px] flex-shrink-0 cursor-pointer"
                                onClick={() => setIsSubscriptionModalVisible(true)}
                            >
                                <NewColorRoundButton
                                    text="이용권 발급하기"
                                    fontType={'CaptionRegular'}
                                    width={112}
                                    height={24}
                                    bgColor="primary"
                                    textColor="white"
                                />
                            </Col>
                        </Row>
                    ) : (
                        <CaptionLight className="text-[#94A3B8] mb-[16px]" style={{ fontSize: 12 }}>
                            ※ 등록된 시설 상품이 없습니다. [시설 관리] 메뉴에서 상품을 먼저 등록해주세요.
                        </CaptionLight>
                    )}

                    {/* 구분선 */}
                    <div className="w-full h-[1px] bg-[#E2E8F0] mb-[24px]" />

                    {/* ━━━ 하단: 메시지 체크 + 등록 버튼 ━━━ */}
                    <Row className="items-center justify-between">
                        <Row className="items-start">
                            <CustomCheckbox
                                checked={isSendMessage}
                                onChange={() => setIsSendMessage((prev) => !prev)}
                                size={24}
                                className="mr-[16px]"
                            />
                            <Col>
                                <Row>
                                    <Body3Regular className="text-LAVEL_3 mr-[4px]">회원님께</Body3Regular>
                                    <Body3Bold className="text-LAVEL_3 mr-[4px]">라포 앱 연결 요청 메시지</Body3Bold>
                                    <Body3Regular>보내기</Body3Regular>
                                </Row>
                                <CaptionLight className="text-LAVEL_3">
                                    회원님께 메시지가 오지 않았다면, 휴대폰 번호가 정확하게 입력되었는지 확인해 주세요!
                                </CaptionLight>
                            </Col>
                        </Row>
                        <Col
                            className={`flex items-center justify-center rounded-[48px] w-[192px] h-[56px] ${
                                isDisabled
                                    ? 'bg-[#A5ABB5] text-[#EDF4FF] cursor-not-allowed'
                                    : 'bg-gradient-to-r from-[#3D73DD] to-[#6327E2] text-[#FDFEFF] cursor-pointer'
                            }`}
                            aria-disabled={isDisabled}
                            onClick={onClickMemberCreate}
                        >
                            <Body1Regular>회원 등록 완료</Body1Regular>
                        </Col>
                    </Row>
                </Col>
            </Col>

            {/* 수강권 생성 모달 (PT) */}
            {isModalMembershipCreateVisible && (
                <ModalMembershipCreate
                    setIsVisible={setIsModalMembershipCreateVisible}
                    onClickCreateNewMembership={() => setIsRegisterPassModal(true)}
                    memberName={name}
                    coachName={selectCoach?.value}
                    coachId={selectCoach?.code}
                    memberId={undefined}
                    createAlert={createAlert}
                    onMembershipCreateSuccess={onMembershipCreateSuccess}
                />
            )}
            {isRegisterPassModal && (
                <ModalRegisterPass
                    modalVisible={isRegisterPassModal}
                    setModalVisible={setIsRegisterPassModal}
                    createAlert={createAlert}
                    onSuccessCreate={() => {}}
                />
            )}

            {/* 이용권 등록 모달 (수집 모드 — 회원 등록 완료 시 함께 서버 등록) */}
            {isSubscriptionModalVisible && (
                <SubscriptionRegisterModal
                    isVisible={isSubscriptionModalVisible}
                    onClose={() => setIsSubscriptionModalVisible(false)}
                    centerId={authObject?.centerId ?? ''}
                    createAlert={createAlert}
                    onCollect={onCollectSubscription}
                />
            )}

            {/* 사업자 정보 (카드사 심사용) */}
            <div style={{ width: '100%', marginTop: 40 }}>
                <BusinessInfoFooter />
            </div>
        </Col>
    );
};

export default MemberTemporaryPage;
